import React, { useCallback, useMemo, useState } from 'react';

import type { IntakeFormDefinition, IntakeFormFileValue, IntakeFormValues } from '@aline/common';
import {
  ALINE_TOOL_NAMES,
  IntakeFormDefinitionSchema,
  IntakeFormValuesSchema,
  validateIntakeFormValues,
  WorkflowLaunchTemplateInputSchema,
} from '@aline/common';
import type { LaunchDocumentChoices, LaunchFormSpec } from '@aline/common/chat/launch-form';
import {
  getInitialLaunchDocumentChoices,
  getIsReplacementFileName,
  getLaunchFormDocumentFields,
  LAUNCH_FORM_COPY,
  LaunchFormSpecSchema,
} from '@aline/common/chat/launch-form';
import { keepSuggestedAlineFieldValues } from '@aline/common/launch-field-suggestion';
import {
  getHasLaunchSelectionError,
  LAUNCH_SELECTION_DISABLED_REASON,
} from '@aline/common/launch-fields/selection';
import { Divider, Grid, Skeleton, Typography } from '@mui/material';
import * as Sentry from '@sentry/react';
import { useSnackbar } from 'notistack';
import { AlineInput } from 'src/AlineInput';
import { AccountsRolesAutoComplete } from 'src/components/AccountsRolesAutoComplete';
import { useChatAdapter } from 'src/components/ai/chat/ChatPanelMarkdown/ChatPanelMarkdown';
import { useGetUnassignedRoles } from 'src/components/AssignRoleList';
import type { RoleAssignmentMap } from 'src/components/AssignRoleList';
import type { AssignRoleRowProps } from 'src/components/AssignRoleRow';
import { useAssignRoleRow } from 'src/components/AssignRoleRow';
import { UploadFilesPicker } from 'src/components/FilestackPicker';
import type { LaunchDocumentUploadSlotProps } from 'src/components/launch-form/LaunchDocumentsSection';
import { LaunchDocumentsSection } from 'src/components/launch-form/LaunchDocumentsSection';
import {
  AssignRoleHeader,
  AssignRoleListLoading,
} from 'src/components/quick-start/components/AssignRoles';
import { getShouldShowDocumentFieldsSection } from 'src/components/quick-start/components/launch-template-visibility';
import { LaunchDocumentFields } from 'src/components/quick-start/components/LaunchDocumentFieldsStep';
import { draftFieldsFromDefinition } from 'src/components/workflow/workflow-detail/intake-form/intake-form-fields';
import { IntakeFormFields } from 'src/components/workflow/workflow-detail/intake-form/IntakeFormFields';
import { Apollo } from 'src/gql/generated';
import {
  FileStackDomContainer,
  pdfMimeTypes,
  wordMimeTypes,
} from 'src/helpers/integrations/filestack';
import { getTypographyForSx } from 'src/theme';
import { makeStyles } from 'tss-react/mui';
import { z } from 'zod';

import {
  ApprovalCard,
  CardPrimaryAction,
  CardSecondaryAction,
  PendingInteractionCardChrome,
} from './AskUser';
import { getInitialIntakeFormValues } from './WorkflowCollectIntakeFormCard';

const useStyles = makeStyles()(theme => ({
  assignAutocomplete: {
    maxWidth: 480,
  },
  error: {
    ...getTypographyForSx(theme.typography.body2),
    color: theme.palette.error.main,
    fontSize: 12,
  },
  intakeFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    minWidth: 0,
    overflowX: 'hidden',
  },
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2.5),
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  sectionTitle: {
    ...getTypographyForSx(theme.typography.subtitle1),
  },
  skeletonField: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
  },
}));

/** Legacy client-input pause for in-flight collect threads. */
export interface WorkflowCollectLaunchInfoCardProps {
  messageId: string;
  suggestedIntakeValues: IntakeFormValues;
  templateWorkflowId: string;
  toolCallId: string;
  maxHeight?: number | null;
  suggestedAlineFieldValues?: Record<string, string>;
  suggestedRoleAssignments?: Record<string, string>;
}

/** Launch-template approval pause — collect requirements then approve to execute. */
export interface WorkflowLaunchTemplateApprovalCardProps {
  approvalId: string;
  input: unknown;
  messageId: string;
  maxHeight?: number | null;
}

type LaunchDocumentFieldsProps = React.ComponentProps<typeof LaunchDocumentFields>;

interface LaunchRoles {
  filteredRoleAssignments: ReturnType<typeof useGetUnassignedRoles>['filteredRoleAssignments'];
  getInitialValues: ReturnType<typeof useGetUnassignedRoles>['getInitialValues'];
  handleRowChange: ReturnType<typeof useGetUnassignedRoles>['handleRowChange'];
  roleAssignmentMap: RoleAssignmentMap;
}

type LaunchSubmitMode =
  | { kind: 'clientTool'; messageId: string; toolCallId: string }
  | { approvalId: string; baseInput: unknown; kind: 'approval'; messageId: string };

/**
 * Loads the template's launch requirements, then mounts a keyed ready card so
 * answers seed once via useState. Apollo hands back fresh references on every
 * cache write (chat streaming writes constantly), so seeding from an effect
 * would wipe in-progress answers.
 */
export function WorkflowCollectLaunchInfoCard({
  maxHeight,
  messageId,
  suggestedAlineFieldValues = {},
  suggestedIntakeValues,
  suggestedRoleAssignments = {},
  templateWorkflowId,
  toolCallId,
}: WorkflowCollectLaunchInfoCardProps) {
  return (
    <LaunchInfoRequirementsLoader
      maxHeight={maxHeight}
      submitMode={{ kind: 'clientTool', messageId, toolCallId }}
      suggestedAlineFieldValues={suggestedAlineFieldValues}
      suggestedIntakeValues={suggestedIntakeValues}
      suggestedRoleAssignments={suggestedRoleAssignments}
      templateWorkflowId={templateWorkflowId}
    />
  );
}

export function WorkflowLaunchTemplateApprovalCard({
  approvalId,
  input,
  maxHeight,
  messageId,
}: WorkflowLaunchTemplateApprovalCardProps) {
  const parsed = WorkflowLaunchTemplateInputSchema.safeParse(input);
  if (!parsed.success) {
    return (
      <ApprovalCard
        approvalId={approvalId}
        input={input}
        maxHeight={maxHeight}
        messageId={messageId}
        toolName={ALINE_TOOL_NAMES.workflowLaunchTemplate}
      />
    );
  }

  return (
    <LaunchInfoRequirementsLoader
      maxHeight={maxHeight}
      submitMode={{ approvalId, baseInput: parsed.data, kind: 'approval', messageId }}
      suggestedAlineFieldValues={parsed.data.suggestedAlineFieldValues ?? {}}
      suggestedIntakeValues={parsed.data.suggestedIntakeValues ?? {}}
      suggestedRoleAssignments={parsed.data.suggestedRoleAssignments ?? {}}
      templateWorkflowId={parsed.data.templateWorkflowId}
    />
  );
}

function LaunchInfoRequirementsLoader({
  maxHeight,
  submitMode,
  suggestedAlineFieldValues,
  suggestedIntakeValues,
  suggestedRoleAssignments,
  templateWorkflowId,
}: {
  submitMode: LaunchSubmitMode;
  suggestedAlineFieldValues: Record<string, string>;
  suggestedIntakeValues: IntakeFormValues;
  suggestedRoleAssignments: Record<string, string>;
  templateWorkflowId: string;
  maxHeight?: number | null;
}) {
  const { classes } = useStyles();
  const {
    data: intakeData,
    error: intakeError,
    loading: intakeLoading,
  } = Apollo.useWorkflowIntakeFormQuery({
    variables: { workflowId: templateWorkflowId },
  });
  const {
    filteredRoleAssignments,
    getInitialValues,
    handleRowChange,
    loading: rolesLoading,
    roleAssignmentMap,
  } = useGetUnassignedRoles({
    entityId: templateWorkflowId,
    entityType: 'Workflow',
    filterBy: 'unassigned',
    // Seeds once on mount, so later suggestion-object churn cannot clobber edits.
    initialRoleAssignmentMap: getInitialRoleAssignmentMap(suggestedRoleAssignments),
    onListChange: () => undefined,
  });
  const template = intakeData?.workflow?.intakeFormTemplate ?? null;
  const parsedDefinition = useMemo(
    () => IntakeFormDefinitionSchema.safeParse(template?.formDefinition),
    [template?.formDefinition]
  );
  const parsedDefaults = useMemo(
    () => IntakeFormValuesSchema.safeParse(template?.defaultValues),
    [template?.defaultValues]
  );
  const {
    data: launchFormData,
    error: launchFormError,
    loading: launchFormLoading,
  } = Apollo.useLaunchFormContextQuery({
    fetchPolicy: 'network-only',
    skip: submitMode.kind !== 'approval',
    variables: {
      approvalId: submitMode.kind === 'approval' ? submitMode.approvalId : '',
      messageId: submitMode.messageId,
    },
  });
  const launchForm = useMemo(() => {
    const parsed = LaunchFormSpecSchema.safeParse(launchFormData?.launchFormContext);
    return parsed.success ? parsed.data : null;
  }, [launchFormData?.launchFormContext]);
  const intakeDefinition = parsedDefinition.success ? parsedDefinition.data : null;
  const alineFields = intakeData?.workflow?.intakeFields ?? [];

  if (intakeLoading || rolesLoading || launchFormLoading) {
    return (
      <PendingInteractionCardChrome
        expanded
        maxHeight={maxHeight}
        title={submitMode.kind === 'approval' ? 'Collect info and launch' : 'Launch information'}
      >
        <LaunchInfoLoading />
      </PendingInteractionCardChrome>
    );
  }

  if (intakeError) {
    return (
      <PendingInteractionCardChrome
        expanded
        maxHeight={maxHeight}
        title={submitMode.kind === 'approval' ? 'Collect info and launch' : 'Launch information'}
      >
        <Typography className={classes.error}>
          The template launch information could not be loaded. Please try again.
        </Typography>
      </PendingInteractionCardChrome>
    );
  }

  return (
    <ReadyWorkflowCollectLaunchInfoCard
      key={submitMode.kind === 'clientTool' ? submitMode.toolCallId : submitMode.approvalId}
      alineFields={alineFields}
      documentsList={
        intakeData?.workflow?.files.map(({ document }) => ({
          documentId: document.id,
          name: document.name,
        })) ?? []
      }
      initialAlineFieldValues={Object.entries(
        keepSuggestedAlineFieldValues(alineFields, suggestedAlineFieldValues)
      ).map(([fieldId, value]) => ({ fieldId, value }))}
      initialIntakeValues={
        intakeDefinition
          ? getInitialIntakeFormValues(intakeDefinition, parsedDefaults, suggestedIntakeValues)
          : {}
      }
      intakeDefinition={intakeDefinition}
      launchForm={launchForm}
      launchFormFailed={submitMode.kind === 'approval' && (Boolean(launchFormError) || !launchForm)}
      maxHeight={maxHeight}
      roles={{ filteredRoleAssignments, getInitialValues, handleRowChange, roleAssignmentMap }}
      submitMode={submitMode}
    />
  );
}

function ReadyWorkflowCollectLaunchInfoCard({
  alineFields,
  documentsList,
  initialAlineFieldValues,
  initialIntakeValues,
  intakeDefinition,
  launchForm,
  launchFormFailed,
  maxHeight,
  roles,
  submitMode,
}: {
  alineFields: LaunchDocumentFieldsProps['alineFields'];
  documentsList: LaunchDocumentFieldsProps['documentsList'];
  initialAlineFieldValues: Apollo.AlineFieldValueInput[];
  initialIntakeValues: IntakeFormValues;
  intakeDefinition: IntakeFormDefinition | null;
  /** The Documents section of a template launch; null for the legacy collect pause. */
  launchForm: LaunchFormSpec | null;
  /** The Documents section could not load, so the launch cannot be confirmed. */
  launchFormFailed: boolean;
  roles: LaunchRoles;
  submitMode: LaunchSubmitMode;
  maxHeight?: number | null;
}) {
  const { classes } = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { addToolApprovalResponse, addToolOutput } = useChatAdapter();
  const [expanded, setExpanded] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alineFieldValues, setAlineFieldValues] = useState(initialAlineFieldValues);
  const [intakeValues, setIntakeValues] = useState(initialIntakeValues);
  const [documentChoices, setDocumentChoices] = useState<LaunchDocumentChoices | null>(() =>
    launchForm ? getInitialLaunchDocumentChoices(launchForm) : null
  );
  const [workflowName, setWorkflowName] = useState(launchForm?.workflowName ?? '');
  const visibleAlineFields = useMemo(
    () =>
      documentChoices
        ? getLaunchFormDocumentFields({
            choices: documentChoices,
            intakeFields: alineFields,
            templateDocumentIds: documentsList.map(document => document.documentId),
          })
        : alineFields,
    [alineFields, documentChoices, documentsList]
  );
  const [deleteDocuments] = Apollo.useDeleteDocumentsMutation();
  const { filteredRoleAssignments, getInitialValues, handleRowChange, roleAssignmentMap } = roles;
  const intakeFields = useMemo(
    () => (intakeDefinition ? draftFieldsFromDefinition(intakeDefinition) : []),
    [intakeDefinition]
  );
  const intakeValidation = intakeDefinition
    ? validateIntakeFormValues(intakeDefinition, intakeValues)
    : null;
  const valueByFieldId = useMemo(
    () => new Map(alineFieldValues.map(entry => [entry.fieldId, entry.value])),
    [alineFieldValues]
  );
  const hasRoles = filteredRoleAssignments.length > 0;
  const hasDocumentFields = getShouldShowDocumentFieldsSection(visibleAlineFields.length);
  const hasIntake = intakeFields.length > 0;
  const submitDisabledReason = getSubmitDisabledReason({
    alineFields: visibleAlineFields,
    intakeValidation,
    launchFormFailed,
    submitting,
    valueByFieldId,
    workflowName: launchForm ? workflowName : null,
  });
  const isApproval = submitMode.kind === 'approval';

  const removeFile = useCallback(
    async (file: IntakeFormFileValue) => {
      await deleteDocuments({ variables: { input: { documentIds: [file.documentId] } } });
    },
    [deleteDocuments]
  );

  /**
   * Only send assignments for placeholders this template actually has. The map is seeded from the
   * model's suggestions and from threads started before role ids became permission ids, so a key
   * can be a well-formed UUID that names nothing here. The server rejects an id that is not one of
   * the template's placeholders, which would fail the whole launch over a stale suggestion, and
   * the Slack card filters the same way.
   */
  const knownPermissionIds = new Set(
    filteredRoleAssignments.map(assignment => assignment.assigneeRole.permissionId)
  );
  const visibleFieldIds = new Set(visibleAlineFields.map(field => field.fieldId));
  const buildCollectedPayload = () => ({
    alineFieldValues: Object.fromEntries(
      alineFieldValues
        .filter(entry => entry.value.trim() !== '' && visibleFieldIds.has(entry.fieldId))
        .map(entry => [entry.fieldId, entry.value])
    ),
    instanceAssignments: Object.entries(roleAssignmentMap).flatMap(([permissionId, details]) =>
      knownPermissionIds.has(permissionId) ? [{ ...details, permissionId }] : []
    ),
    ...(intakeValidation ? { intakeFormValues: intakeValidation.sanitizedValues } : {}),
  });

  const submit = async () => {
    if (submitDisabledReason) return;
    try {
      setSubmitting(true);
      const collected = buildCollectedPayload();
      if (submitMode.kind === 'approval') {
        const base =
          typeof submitMode.baseInput === 'object' && submitMode.baseInput !== null
            ? submitMode.baseInput
            : {};

        await addToolApprovalResponse({
          approved: true,
          id: submitMode.approvalId,
          input: {
            ...base,
            ...collected,
            ...documentChoices,
            ...(launchForm ? { name: workflowName.trim() } : {}),
          },
          messageId: submitMode.messageId,
        });
        return;
      }

      await addToolOutput({
        messageId: submitMode.messageId,
        output: { ...collected, status: 'submitted' as const },
        tool: 'workflowCollectLaunchInfo',
        toolCallId: submitMode.toolCallId,
      });
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar(
        isApproval
          ? 'Failed to launch the workflow. Please try again.'
          : 'Failed to submit launch information. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  const skip = async () => {
    if (submitting) return;
    try {
      setSubmitting(true);
      if (submitMode.kind === 'approval') {
        await addToolApprovalResponse({
          approved: false,
          id: submitMode.approvalId,
          messageId: submitMode.messageId,
        });
        return;
      }

      await addToolOutput({
        messageId: submitMode.messageId,
        output: { status: 'skipped' },
        tool: 'workflowCollectLaunchInfo',
        toolCallId: submitMode.toolCallId,
      });
    } catch (error) {
      Sentry.captureException(error);
      enqueueSnackbar(
        isApproval
          ? 'Failed to skip the launch. Please try again.'
          : 'Failed to skip launch information. Please try again.',
        { variant: 'error' }
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PendingInteractionCardChrome
      expanded={expanded}
      footer={
        <>
          <CardSecondaryAction disabled={submitting} onClick={() => void skip()}>
            Skip
          </CardSecondaryAction>
          <CardPrimaryAction
            disabled={submitting}
            disabledReason={submitDisabledReason}
            loading={submitting}
            onClick={() => void submit()}
          >
            {isApproval ? LAUNCH_FORM_COPY.launch : 'Submit'}
          </CardPrimaryAction>
        </>
      }
      maxHeight={maxHeight}
      title={isApproval ? LAUNCH_FORM_COPY.title : 'Launch information'}
      toggleLabel={isApproval ? 'launch approval' : 'launch information'}
      onToggleExpanded={() => setExpanded(current => !current)}
    >
      <div className={classes.root}>
        {Boolean(launchForm) && (
          <section className={classes.section}>
            <Typography className={classes.sectionTitle}>
              {LAUNCH_FORM_COPY.workflowName}
            </Typography>
            <AlineInput
              inputProps={{ 'aria-label': LAUNCH_FORM_COPY.workflowName }}
              value={workflowName}
              onChange={event => setWorkflowName(event.target.value)}
            />
          </section>
        )}
        {launchForm && documentChoices && (
          <LaunchDocumentsSection
            choices={documentChoices}
            disabled={submitting}
            renderUpload={renderInAppLaunchDocumentUpload}
            spec={launchForm}
            onChange={setDocumentChoices}
          />
        )}
        {launchFormFailed && (
          <Typography className={classes.error}>
            The documents for this launch could not be loaded. Please try again.
          </Typography>
        )}
        {hasRoles && (
          <LaunchRolesSection
            assignments={filteredRoleAssignments}
            getInitialValues={getInitialValues}
            roleAssignmentMap={roleAssignmentMap}
            onChange={handleRowChange}
          />
        )}
        {hasIntake && intakeDefinition && (
          <section className={classes.section}>
            <Typography className={classes.sectionTitle}>Intake questions</Typography>
            <div className={classes.intakeFields}>
              <IntakeFormFields
                fields={intakeFields}
                formDefinition={intakeDefinition}
                values={intakeValues}
                onChange={(name, value) =>
                  setIntakeValues(current => ({ ...current, [name]: value }))
                }
                onRemoveFile={removeFile}
              />
            </div>
          </section>
        )}
        {hasDocumentFields && (
          <section className={classes.section}>
            <Typography className={classes.sectionTitle}>Document fields</Typography>
            <LaunchDocumentFields
              alineFields={visibleAlineFields}
              documentsList={documentsList}
              valueByFieldId={valueByFieldId}
              onChange={(fieldId, value) =>
                setAlineFieldValues(current => upsertAlineFieldValue(current, fieldId, value))
              }
            />
          </section>
        )}
      </div>
    </PendingInteractionCardChrome>
  );
}

function LaunchRolesSection({
  assignments,
  getInitialValues,
  onChange,
  roleAssignmentMap,
}: {
  assignments: ReturnType<typeof useGetUnassignedRoles>['filteredRoleAssignments'];
  getInitialValues: ReturnType<typeof useGetUnassignedRoles>['getInitialValues'];
  onChange: ReturnType<typeof useGetUnassignedRoles>['handleRowChange'];
  roleAssignmentMap: RoleAssignmentMap;
}) {
  const { classes } = useStyles();
  return (
    <section className={classes.section}>
      <Typography className={classes.sectionTitle}>Assign roles</Typography>
      <AssignRoleHeader />
      <Divider />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        {assignments.map(assignment => (
          <LaunchAssignRoleRow
            key={assignment.assigneeRole.permissionId}
            assignment={assignment}
            initialUserOrEmail={getInitialValues(
              roleAssignmentMap[assignment.assigneeRole.permissionId]
            )}
            onChange={onChange}
          />
        ))}
      </Grid>
    </section>
  );
}

function LaunchAssignRoleRow({ assignment, initialUserOrEmail, onChange }: AssignRoleRowProps) {
  const { classes } = useStyles();
  const { handleAutocompleteChange, userOrSuggested } = useAssignRoleRow({
    assignment,
    initialUserOrEmail,
    onChange,
  });

  return (
    <>
      <Grid size={3}>
        <Typography variant="body1">
          {assignment.assigneeRole.roleName || 'Untitled Role'}:
        </Typography>
      </Grid>
      <Grid size={9}>
        <AccountsRolesAutoComplete
          suggestInviteEmail
          className={classes.assignAutocomplete}
          includeOptions="users"
          placeholder="Search users or type an email..."
          value={userOrSuggested}
          variant="avatar"
          onChange={handleAutocompleteChange}
        />
      </Grid>
    </>
  );
}

function LaunchInfoLoading() {
  const { classes } = useStyles();
  return (
    <div aria-busy="true" className={classes.intakeFields}>
      <AssignRoleListLoading />
      <div className={classes.skeletonField}>
        <Skeleton height={14} variant="text" width="44%" />
        <Skeleton height={36} variant="rounded" width="100%" />
      </div>
      <div className={classes.skeletonField}>
        <Skeleton height={14} variant="text" width="56%" />
        <Skeleton height={36} variant="rounded" width="100%" />
      </div>
    </div>
  );
}

/** An upload slot of the Documents section, through Filestack like the rest of the app. */
function renderInAppLaunchDocumentUpload(props: LaunchDocumentUploadSlotProps) {
  return <InAppLaunchDocumentUpload {...props} />;
}

function InAppLaunchDocumentUpload({
  onCancel,
  onUploaded,
  target,
}: LaunchDocumentUploadSlotProps) {
  const { enqueueSnackbar } = useSnackbar();
  const isReplacement = Boolean(target.templateDocumentId);
  return (
    <UploadFilesPicker
      open
      domContainer={FileStackDomContainer.LaunchFormDocument}
      // The form needs the uploaded document itself, never a new version of an existing one.
      offerUploadAsVersion={false}
      pickerOpts={{
        accept: isReplacement ? [...pdfMimeTypes, ...wordMimeTypes] : undefined,
        afterUploadDone: result => {
          const file = result.data?.createFiles?.at(0);
          if (!file) return onCancel();
          if (isReplacement && !getIsReplacementFileName(file.name)) {
            enqueueSnackbar(LAUNCH_FORM_COPY.wrongFileType, { variant: 'error' });
            return onCancel();
          }
          onUploaded({ documentId: file.id, name: file.name });
        },
        maxFiles: 1,
        onClose: onCancel,
        props: { documentVariant: Apollo.DocumentVariant.Document, isSigned: false },
      }}
    />
  );
}

function getSubmitDisabledReason({
  alineFields,
  intakeValidation,
  launchFormFailed,
  submitting,
  valueByFieldId,
  workflowName,
}: {
  alineFields: LaunchDocumentFieldsProps['alineFields'];
  intakeValidation: ReturnType<typeof validateIntakeFormValues> | null;
  launchFormFailed: boolean;
  submitting: boolean;
  valueByFieldId: ReadonlyMap<string, string>;
  /** Null when the card has no name field. */
  workflowName: string | null;
}) {
  if (submitting) return 'Submitting...';
  if (launchFormFailed) return 'The launch documents could not be loaded';
  if (workflowName !== null && !workflowName.trim()) return 'Name the workflow before launching';
  if (intakeValidation && intakeValidation.missingRequiredFields.length > 0)
    return 'Answer required questions before submitting';
  if (intakeValidation?.fieldErrors && Object.keys(intakeValidation.fieldErrors).length > 0)
    return 'Fix invalid fields before submitting';
  if (alineFields.some(field => field.required && !valueByFieldId.get(field.fieldId)?.trim()))
    return 'Fill in required fields before submitting';
  if (getHasLaunchSelectionError(alineFields, valueByFieldId))
    return LAUNCH_SELECTION_DISABLED_REASON;
  return '';
}

/**
 * The agent's role hints are free text, and only an email can be turned into a
 * concrete assignment without a user lookup. Name-only hints are dropped so the
 * submitted assignments never contain a value the launch mutation would reject.
 */
function getInitialRoleAssignmentMap(
  suggestedRoleAssignments: Record<string, string>
): RoleAssignmentMap {
  const entries = Object.entries(suggestedRoleAssignments).flatMap<[string, { email: string }]>(
    ([permissionId, hint]) => {
      const email = z.email().safeParse(hint.trim().toLowerCase());
      if (!z.uuid().safeParse(permissionId).success || !email.success) return [];
      return [[permissionId, { email: email.data }]];
    }
  );
  return Object.fromEntries(entries);
}

function upsertAlineFieldValue(
  current: Apollo.AlineFieldValueInput[],
  fieldId: string,
  value: string
): Apollo.AlineFieldValueInput[] {
  const without = current.filter(entry => entry.fieldId !== fieldId);
  if (value === '') return without;
  return [...without, { fieldId, value }];
}
